import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronLeft, ExternalLink } from 'lucide-react';
import {
  Avatar,
  backLinkVariants,
  Button,
  Card,
  cn,
  EmptyState,
  PageBody,
  SectionLabel,
  Spinner,
  Switch,
} from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { MessageDisplay } from '@/components/messages/MessageDisplay.tsx';
import { SeverityPill } from '@/components/conversationReview/SeverityPill.tsx';
import { MemoryViewerDialog } from '@/components/users/MemoryViewerDialog.tsx';
import { requireInternalOrganisation } from '@/lib/staffGate.ts';
import {
  type ConversationReview,
  fetchReview,
  fetchReviewMessages,
  markReviewHandled,
  reopenReview,
  type ReviewFinding,
} from '@/lib/conversationReviews.ts';
import type { Message } from '@/types/messages.ts';
import { formatRelativeTime } from '@/utils/dateUtils.ts';
import { findQuotedMessage } from '@/components/conversationReview/findQuotedMessage.ts';
import { findingClassLabel } from '@/components/conversationReview/findingClasses.ts';
import { UserMessagesLink } from '@/components/conversationReview/UserMessagesLink.tsx';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';

const DEBUG_MODE_KEY = 'debug-mode';
const HIGHLIGHT_MS = 2_000;

export const Route = createFileRoute('/_app/conversation-reviews/$id')({
  staticData: { orgScope: 'none' },
  beforeLoad: ({ context }) => requireInternalOrganisation(context.queryClient),
  component: ConversationReviewDetailRoute,
});

function ConversationReviewDetailRoute() {
  return <ConversationReviewDetail id={Route.useParams().id} />;
}

function readDebugMode(): boolean {
  try {
    const stored = localStorage.getItem(DEBUG_MODE_KEY);
    return stored ? (JSON.parse(stored) as boolean) : false;
  } catch {
    /* localStorage may be unavailable (Safari private mode) */
    return false;
  }
}

/**
 * One reviewed conversation: what the reviewer found in it, beside the
 * transcript it found it in.
 */
export function ConversationReviewDetail({ id }: { id: string }) {
  const [debug, setDebug] = useState(readDebugMode);
  const [memories, setMemories] = useState<{ userId: string; handles: string[] } | null>(
    null,
  );
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  useEffect(() => {
    try {
      localStorage.setItem(DEBUG_MODE_KEY, JSON.stringify(debug));
    } catch {
      /* localStorage may be unavailable (Safari private mode) */
    }
  }, [debug]);

  const review = useQuery({
    queryKey: ['conversationReview', id],
    queryFn: () => fetchReview(id),
  });
  const messages = useQuery({
    queryKey: ['conversationReviewMessages', id],
    queryFn: () => fetchReviewMessages(id),
  });

  if (review.error instanceof ApiError && review.error.status === 404) {
    return (
      <>
        <BackToList />
        <PageHeader title='Review detail' />
        <PageBody className='mt-3.5'>
          <Card>
            <EmptyState
              title='Review not found'
              description='It may have been deleted.'
            />
          </Card>
        </PageBody>
      </>
    );
  }

  if (review.error) throw review.error;
  if (messages.error) throw messages.error;

  if (!review.data) {
    return (
      <>
        <BackToList />
        <PageHeader title='Review detail' />
        <PageBody className='mt-3.5'>
          <Spinner className='size-5' />
        </PageBody>
      </>
    );
  }

  const data = review.data;

  function showQuotedMessage(quoted: Message) {
    document
      .getElementById(messageElementId(quoted.id))
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlighted(quoted.id);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlighted(null), HIGHLIGHT_MS);
  }

  return (
    <>
      <BackToList />
      <PageHeader title={data.session_title || 'Review detail'} />
      <PageBody className='mt-3.5'>
        <div className='flex flex-wrap items-center gap-3'>
          <SeverityPill severity={data.severity} />
          <span className='text-sm text-ink-muted'>
            {formatRelativeTime(data.created_at)}
            <span className='px-1.5'>·</span>
            {new Date(data.created_at).toLocaleString()}
          </span>
        </div>

        <div className='mt-5 grid grid-cols-3 items-start gap-5 max-mobile:grid-cols-1'>
          <div className='flex flex-col gap-5'>
            <ReviewFindingsCard
              review={data}
              messages={messages.data ?? []}
              onShowQuotedMessage={showQuotedMessage}
            />
            <ReviewSessionCard review={data} />
          </div>

          <Card as='section' className='col-span-2 max-mobile:col-span-1'>
            <div className='flex items-center justify-between gap-3'>
              <SectionLabel as='h2'>Conversation</SectionLabel>
              <div className='flex items-center gap-4'>
                <div className='flex items-center gap-2'>
                  <Switch
                    id='debug-mode'
                    size='sm'
                    checked={debug}
                    onCheckedChange={setDebug}
                  />
                  <label htmlFor='debug-mode' className='text-sm text-ink-secondary'>
                    Debug
                  </label>
                </div>
                <span className='text-xs tabular-nums text-ink-muted'>
                  {messages.data?.length ?? 0} messages
                </span>
              </div>
            </div>
            <div className='mt-3.5 flex max-h-160 flex-col gap-3.5 overflow-y-auto pr-1'>
              {messages.isPending && <Spinner className='size-5' />}
              {messages.data?.map((message) => (
                <div
                  key={message.id}
                  id={messageElementId(message.id)}
                  data-highlighted={highlighted === message.id || undefined}
                  className={cn(
                    'rounded-md transition-colors',
                    highlighted === message.id && 'bg-accent-tint ring-2 ring-ink',
                  )}
                >
                  <MessageDisplay
                    message={message}
                    debug={debug}
                    onOpenMemories={(handles) =>
                      setMemories({ userId: message.user_id, handles })
                    }
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </PageBody>

      {memories && (
        <MemoryViewerDialog
          userId={memories.userId}
          offeredHandles={memories.handles}
          open
          onOpenChange={(open) => {
            if (!open) setMemories(null);
          }}
        />
      )}
    </>
  );
}

function messageElementId(messageId: string): string {
  return `review-message-${messageId}`;
}

/* The way back, in the gap PageBody leaves above its first child. */
function BackToList() {
  return (
    <Link
      to='/conversation-reviews'
      className={backLinkVariants({ className: 'mb-2.5' })}
    >
      <ChevronLeft aria-hidden='true' />
      Conversation reviews
    </Link>
  );
}

interface ReviewFindingsCardProps {
  review: ConversationReview;
  /** The conversation's messages, once they are in: a finding is shown in them. */
  messages: Message[];
  onShowQuotedMessage: (quoted: Message) => void;
}

function ReviewFindingsCard({
  review,
  messages,
  onShowQuotedMessage,
}: ReviewFindingsCardProps) {
  return (
    <Card as='section'>
      <SectionLabel as='h2'>Findings</SectionLabel>

      {review.findings.length > 0 ? (
        <ul className='mt-3.5 flex flex-col gap-2.5'>
          {review.findings.map((finding, index) => {
            const quoted = findQuotedMessage(messages, finding, review.user_timezone);
            return (
              <FindingCard
                key={index}
                finding={finding}
                onShowInConversation={
                  quoted ? () => onShowQuotedMessage(quoted) : undefined
                }
              />
            );
          })}
        </ul>
      ) : (
        <p className='mt-3.5 flex items-center gap-2 rounded-md bg-accent-tint px-3.5 py-2.5 text-sm text-success'>
          <Check className='size-3.5 shrink-0' aria-hidden='true' />
          No findings
        </p>
      )}

      <HandledControl review={review} />

      {review.trace_id && (
        <Link
          to='/traces/$traceId'
          params={{ traceId: review.trace_id }}
          target='_blank'
          className='mt-3 inline-flex items-center gap-1 text-sm text-ink-secondary underline underline-offset-2 hover:text-ink'
        >
          <ExternalLink className='size-3.5' aria-hidden='true' />
          Trace
        </Link>
      )}
    </Card>
  );
}

function FindingCard({
  finding,
  onShowInConversation,
}: {
  finding: ReviewFinding;
  /** Left out when no turn of the conversation can be traced to the finding. */
  onShowInConversation?: () => void;
}) {
  return (
    <li>
      <Card tone='quiet' padding='none' className='p-3.5'>
        <h3 className='text-sm font-bold text-ink'>{findingClassLabel(finding.class)}</h3>
        {finding.description && (
          <p className='mt-1 text-sm text-ink-body'>{finding.description}</p>
        )}
        {finding.evidence && (
          <blockquote className='mt-2 border-l-2 border-line pl-2.5 text-sm italic text-ink-muted'>
            &ldquo;{finding.evidence}&rdquo;
          </blockquote>
        )}
        {onShowInConversation && (
          <Button
            size='sm'
            variant='link'
            className='mt-2'
            onClick={onShowInConversation}
          >
            Show in conversation
          </Button>
        )}
      </Card>
    </li>
  );
}

function ReviewSessionCard({ review }: { review: ConversationReview }) {
  return (
    <Card as='section'>
      <SectionLabel as='h2'>Session</SectionLabel>
      <dl className='mt-2.5 flex flex-col gap-2.5 text-sm'>
        <div>
          <dt className='text-xs font-bold tracking-widest text-ink-muted uppercase'>
            User
          </dt>
          <dd className='mt-1'>
            <Link
              to='/users/$id'
              params={{ id: review.user_id }}
              className='inline-flex items-center gap-2 text-ink hover:underline'
            >
              <Avatar size='sm' name={review.user_name?.trim() || '?'} />
              {review.user_name?.trim() || review.user_id}
            </Link>
          </dd>
        </div>
        {review.organisation_name && (
          <div>
            <dt className='text-xs font-bold tracking-widest text-ink-muted uppercase'>
              Organisation
            </dt>
            <dd className='mt-0.5 text-ink'>{review.organisation_name}</dd>
          </div>
        )}
        {review.session_title && (
          <div>
            <dt className='text-xs font-bold tracking-widest text-ink-muted uppercase'>
              Title
            </dt>
            <dd className='mt-0.5 text-ink'>{review.session_title}</dd>
          </div>
        )}
        <div>
          <dt className='text-xs font-bold tracking-widest text-ink-muted uppercase'>
            Reviewed
          </dt>
          <dd className='mt-0.5 text-ink'>{formatRelativeTime(review.created_at)}</dd>
        </div>
      </dl>
      <div className='mt-3.5 border-t border-line pt-3'>
        <UserMessagesLink
          userId={review.user_id}
          className='text-sm text-ink-secondary underline underline-offset-2 hover:text-ink'
        >
          View all user conversations →
        </UserMessagesLink>
      </div>
    </Card>
  );
}

/* Marking a review handled takes it off the default list; reopening puts it back. */
function HandledControl({ review }: { review: ConversationReview }) {
  const queryClient = useQueryClient();
  const handled = review.handled_at !== null;

  const toggle = useMutation({
    mutationFn: () => (handled ? reopenReview(review.id) : markReviewHandled(review.id)),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['conversationReview', review.id] }),
        queryClient.invalidateQueries({ queryKey: ['conversationReviews'] }),
        queryClient.invalidateQueries({ queryKey: ['conversationReviewCounts'] }),
      ]),
  });

  return (
    <div className='mt-3.5 flex flex-col gap-2 border-t border-line pt-3'>
      {review.handled_at && (
        <p className='flex items-center gap-1.5 text-sm text-ink-secondary'>
          <Check className='size-3.5 shrink-0 text-success' aria-hidden='true' />
          Handled
          {review.handled_by_name && <> by {review.handled_by_name}</>}{' '}
          <span title={new Date(review.handled_at).toLocaleString()}>
            {formatRelativeTime(review.handled_at)}
          </span>
        </p>
      )}
      <Button
        size='sm'
        variant={handled ? 'ghost' : 'primary'}
        className='self-start'
        loading={toggle.isPending}
        disabled={toggle.isPending}
        onClick={() => toggle.mutate()}
      >
        {handled ? 'Reopen' : 'Mark as handled'}
      </Button>
      {toggle.error && (
        <p role='alert' className='text-sm font-semibold text-danger'>
          {apiErrorMessage(
            toggle.error,
            handled
              ? 'Failed to reopen the review'
              : 'Failed to mark the review as handled',
          )}
        </p>
      )}
    </div>
  );
}
